import api from './api';
import type {
  BanListResponse,
  GlobalAsnBan,
  GlobalDomainBan,
  GlobalIpBan,
  GlobalIspBan,
  IpBan,
  SuspiciousEntry,
  SuspiciousType,
} from '../types/ban';

// User-level IP Bans (Account-scoped)
export const banService = {
  async getIpBans(params?: {
    search?: string;
    is_active?: boolean;
    page?: number;
  }): Promise<BanListResponse<IpBan>> {
    const response = await api.get('/api/bans/ips', { params });
    return response.data;
  },

  async createIpBan(data: {
    ip_address: string;
    reason?: string;
  }): Promise<{ message: string; ban: IpBan }> {
    const response = await api.post('/api/bans/ips', data);
    return response.data;
  },

  async updateIpBan(
    id: number,
    data: { reason?: string; is_active?: boolean }
  ): Promise<{ message: string; ban: IpBan }> {
    const response = await api.put(`/api/bans/ips/${id}`, data);
    return response.data;
  },

  async deleteIpBan(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/bans/ips/${id}`);
    return response.data;
  },
};

// Admin-level Global Bans
export const adminBanService = {
  // ========== IP Bans ==========
  async getGlobalIpBans(params?: {
    search?: string;
    page?: number;
  }): Promise<BanListResponse<GlobalIpBan>> {
    const response = await api.get('/api/admin/bans/ips', { params });
    return response.data;
  },

  async createGlobalIpBan(data: {
    ip_address: string;
    reason?: string;
  }): Promise<{ message: string; ban: GlobalIpBan }> {
    const response = await api.post('/api/admin/bans/ips', data);
    return response.data;
  },

  async updateGlobalIpBan(
    id: number,
    data: { reason?: string; is_active?: boolean }
  ): Promise<{ message: string; ban: GlobalIpBan }> {
    const response = await api.put(`/api/admin/bans/ips/${id}`, data);
    return response.data;
  },

  async deleteGlobalIpBan(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/bans/ips/${id}`);
    return response.data;
  },

  // ========== Domain Bans ==========
  async getGlobalDomainBans(params?: {
    search?: string;
    page?: number;
  }): Promise<BanListResponse<GlobalDomainBan>> {
    const response = await api.get('/api/admin/bans/domains', { params });
    return response.data;
  },

  async createGlobalDomainBan(data: {
    domain: string;
    reason?: string;
  }): Promise<{ message: string; ban: GlobalDomainBan }> {
    const response = await api.post('/api/admin/bans/domains', data);
    return response.data;
  },

  async updateGlobalDomainBan(
    id: number,
    data: { reason?: string; is_active?: boolean }
  ): Promise<{ message: string; ban: GlobalDomainBan }> {
    const response = await api.put(`/api/admin/bans/domains/${id}`, data);
    return response.data;
  },

  async deleteGlobalDomainBan(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/bans/domains/${id}`);
    return response.data;
  },

  // ========== ISP Bans ==========
  async getGlobalIspBans(params?: {
    search?: string;
    page?: number;
  }): Promise<BanListResponse<GlobalIspBan>> {
    const response = await api.get('/api/admin/bans/isps', { params });
    return response.data;
  },

  async createGlobalIspBan(data: {
    isp_name: string;
    reason?: string;
  }): Promise<{ message: string; ban: GlobalIspBan }> {
    const response = await api.post('/api/admin/bans/isps', data);
    return response.data;
  },

  async updateGlobalIspBan(
    id: number,
    data: { reason?: string; is_active?: boolean }
  ): Promise<{ message: string; ban: GlobalIspBan }> {
    const response = await api.put(`/api/admin/bans/isps/${id}`, data);
    return response.data;
  },

  async deleteGlobalIspBan(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/bans/isps/${id}`);
    return response.data;
  },

  // ========== ASN Bans ==========
  async getGlobalAsnBans(params?: {
    search?: string;
    page?: number;
  }): Promise<BanListResponse<GlobalAsnBan>> {
    const response = await api.get('/api/admin/bans/asns', { params });
    return response.data;
  },

  async createGlobalAsnBan(data: {
    asn: number;
    asn_name?: string;
    reason?: string;
  }): Promise<{ message: string; ban: GlobalAsnBan }> {
    const response = await api.post('/api/admin/bans/asns', data);
    return response.data;
  },

  async updateGlobalAsnBan(
    id: number,
    data: { asn_name?: string; reason?: string; is_active?: boolean }
  ): Promise<{ message: string; ban: GlobalAsnBan }> {
    const response = await api.put(`/api/admin/bans/asns/${id}`, data);
    return response.data;
  },

  async deleteGlobalAsnBan(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/bans/asns/${id}`);
    return response.data;
  },

  // ========== Suspicious / Watchlist ==========
  async getSuspiciousEntries(params?: {
    type?: SuspiciousType;
    search?: string;
  }): Promise<BanListResponse<SuspiciousEntry>> {
    const response = await api.get('/api/admin/suspicious', { params });
    return response.data;
  },

  async createSuspiciousEntry(data: {
    type: SuspiciousType;
    value: string;
    notes?: string;
  }): Promise<{ message: string; entry: SuspiciousEntry }> {
    const response = await api.post('/api/admin/suspicious', data);
    return response.data;
  },

  async updateSuspiciousEntry(
    id: number,
    data: { value?: string; notes?: string }
  ): Promise<{ message: string; entry: SuspiciousEntry }> {
    const response = await api.put(`/api/admin/suspicious/${id}`, data);
    return response.data;
  },

  async deleteSuspiciousEntry(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/suspicious/${id}`);
    return response.data;
  },

  async bulkDeleteSuspicious(ids: number[]): Promise<{ message: string; deleted_count: number }> {
    const response = await api.post('/api/admin/suspicious/bulk-delete', { ids });
    return response.data;
  },

  // ========== Bulk Operations ==========
  async bulkDelete(
    type: string,
    ids: number[]
  ): Promise<{ message: string; deleted_count: number }> {
    const response = await api.post('/api/admin/bans/bulk-delete', {
      type,
      ids,
    });
    return response.data;
  },

  async bulkToggleStatus(
    type: string,
    ids: number[],
    is_active: boolean
  ): Promise<{ message: string; updated_count: number }> {
    const response = await api.post('/api/admin/bans/bulk-toggle-status', {
      type,
      ids,
      is_active,
    });
    return response.data;
  },
};
